import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import type { Storefront, StoreType } from "../../repositories/storefrontRepository";

interface StorefrontConfigProps {
  projectId: string;
  storefronts: Storefront[];
  onSave: (store: StoreType, config: Record<string, unknown>, enabled: boolean, autoDeployOnBuild: boolean) => Promise<void>;
}

interface StoreConfig {
  type: StoreType;
  label: string;
  fields: { key: string; label: string; placeholder: string }[];
  available: boolean;
}

const storeConfigs: StoreConfig[] = [
  {
    type: "steam",
    label: "Steam",
    available: true,
    fields: [
      { key: "app_id", label: "App ID", placeholder: "480" },
      { key: "depot_id_windows", label: "Windows Depot ID", placeholder: "481" },
      { key: "depot_id_linux", label: "Linux Depot ID", placeholder: "482" },
      { key: "depot_id_macos", label: "macOS Depot ID", placeholder: "483" },
    ],
  },
  {
    type: "itch_io",
    label: "itch.io",
    available: false,
    fields: [
      { key: "username", label: "Username", placeholder: "your-username" },
      { key: "game_slug", label: "Game Slug", placeholder: "your-game" },
    ],
  },
  {
    type: "epic",
    label: "Epic Games Store",
    available: false,
    fields: [
      { key: "product_id", label: "Product ID", placeholder: "" },
      { key: "sandbox_id", label: "Sandbox ID", placeholder: "" },
    ],
  },
  {
    type: "gog",
    label: "GOG",
    available: false,
    fields: [
      { key: "game_id", label: "Game ID", placeholder: "" },
    ],
  },
];

// BuildCrate's Steam account email - fetched from server config
const BUILDCRATE_STEAM_EMAIL_DEFAULT = "deploy@buildcrate.games";

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 bg-blue-100 hover:bg-blue-200 px-2 py-0.5 rounded font-mono text-xs transition-colors cursor-pointer"
      title="Click to copy"
    >
      <span className="select-all">{value}</span>
      {copied ? (
        <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
}

function SteamSetupInstructions({ steamEmail }: { steamEmail: string }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <h4 className="font-medium text-blue-900 mb-2">Setup Required</h4>
      <p className="text-sm text-blue-800 mb-3">
        To deploy your game to Steam, you need to add the BuildCrate Steam account as a user with upload permissions in Steamworks:
      </p>
      <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 mb-3">
        <li>Log in to <a href="https://partner.steamgames.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">partner.steamgames.com</a></li>
        <li>Go to <strong>Users & Permissions</strong> &gt; <strong>Manage Users</strong></li>
        <li>Click <strong>Add User</strong></li>
        <li>
          As <strong>Real Name</strong>, enter:{" "}
          <CopyButton value="BuildCrate" />
        </li>
        <li>
          As <strong>Email</strong>, enter:{" "}
          <CopyButton value={steamEmail} />
        </li>
        <li>Grant the following permissions for your app:
          <ul className="list-disc list-inside ml-4 mt-1">
            <li><strong>Edit App Metadata</strong></li>
            <li><strong>Publish App Changes To Steam</strong></li>
          </ul>
        </li>
        <li>Save the changes and wait for the invitation to be accepted</li>
      </ol>
      <p className="text-xs text-blue-700">
        The BuildCrate account will be automatically added within 24 hours of the invitation being sent.
      </p>
    </div>
  );
}

function DisabledStorefrontAccordion({ storeConfig }: { storeConfig: StoreConfig }) {
  return (
    <div className="border rounded-lg opacity-60">
      <div className="w-full px-4 py-3 flex items-center justify-between text-left cursor-not-allowed">
        <div className="flex items-center gap-3">
          <span className="font-medium text-muted-foreground">{storeConfig.label}</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
            Coming Soon
          </span>
        </div>
        <span className="text-muted-foreground">+</span>
      </div>
    </div>
  );
}

function StorefrontAccordion({
  storeConfig,
  storefront,
  onSave,
  steamEmail,
}: {
  storeConfig: StoreConfig;
  storefront?: Storefront;
  onSave: (config: Record<string, unknown>, enabled: boolean, autoDeployOnBuild: boolean) => Promise<void>;
  steamEmail?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<Record<string, string>>(
    (storefront?.config as Record<string, string>) || {}
  );
  const [enabled, setEnabled] = useState(storefront?.enabled || false);
  const [autoDeployOnBuild, setAutoDeployOnBuild] = useState(
    storefront?.autoDeployOnBuild || false
  );
  const [saving, setSaving] = useState(false);
  const [confirmedSteamSetup, setConfirmedSteamSetup] = useState(
    storefront?.config?.steamSetupConfirmed === true
  );

  const isSteam = storeConfig.type === "steam";
  const canSave = !isSteam || confirmedSteamSetup;

  const handleSave = async () => {
    setSaving(true);
    try {
      const configToSave = isSteam
        ? { ...config, steamSetupConfirmed: confirmedSteamSetup }
        : config;
      await onSave(configToSave, enabled, autoDeployOnBuild);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-muted/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium">{storeConfig.label}</span>
          {storefront?.enabled && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
              Enabled
            </span>
          )}
        </div>
        <span className="text-muted-foreground">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t">
          {isSteam && steamEmail && (
            <div className="pt-4">
              <SteamSetupInstructions steamEmail={steamEmail} />
            </div>
          )}

          <div className={isSteam && steamEmail ? "" : "pt-4"}>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Enabled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoDeployOnBuild}
                  onChange={(e) => setAutoDeployOnBuild(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Auto-deploy on build</span>
              </label>
            </div>
          </div>

          {storeConfig.fields.map((field) => (
            <div key={field.key}>
              <Label htmlFor={`${storeConfig.type}-${field.key}`}>
                {field.label}
              </Label>
              <Input
                id={`${storeConfig.type}-${field.key}`}
                value={config[field.key] || ""}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
                placeholder={field.placeholder}
                className="mt-1"
              />
            </div>
          ))}

          {isSteam && (
            <div className="border-t pt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmedSteamSetup}
                  onChange={(e) => setConfirmedSteamSetup(e.target.checked)}
                  className="rounded border-gray-300 mt-0.5"
                />
                <span className="text-sm">
                  I confirm that I have added the BuildCrate Steam account as a user with upload permissions for my app in Steamworks
                </span>
              </label>
            </div>
          )}

          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
          {isSteam && !canSave && (
            <p className="text-xs text-amber-600">
              Please confirm that you have completed the Steam setup above
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function StorefrontConfig({
  projectId,
  storefronts,
  onSave,
}: StorefrontConfigProps) {
  const storefrontMap = new Map(storefronts.map((s) => [s.store, s]));
  const [steamEmail, setSteamEmail] = useState<string>(BUILDCRATE_STEAM_EMAIL_DEFAULT);

  // Fetch the BuildCrate Steam email from server config
  useEffect(() => {
    async function fetchConfig() {
      try {
        const response = await fetch("/api/config/steam", { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          if (data.steamEmail) {
            setSteamEmail(data.steamEmail);
          }
        }
      } catch {
        // Use default email on error
      }
    }
    fetchConfig();
  }, []);

  return (
    <div className="space-y-3">
      {storeConfigs.map((storeConfig) =>
        storeConfig.available ? (
          <StorefrontAccordion
            key={storeConfig.type}
            storeConfig={storeConfig}
            storefront={storefrontMap.get(storeConfig.type)}
            steamEmail={storeConfig.type === "steam" ? steamEmail : undefined}
            onSave={(config, enabled, autoDeployOnBuild) =>
              onSave(storeConfig.type, config, enabled, autoDeployOnBuild)
            }
          />
        ) : (
          <DisabledStorefrontAccordion
            key={storeConfig.type}
            storeConfig={storeConfig}
          />
        )
      )}
    </div>
  );
}
